/**
 * Pagamentos online (#160): onboarding Stripe Connect da conta do
 * restaurante (Express, direct charges) e acompanhamento do estado
 * (charges/payouts/dados submetidos) espelhado do webhook account.updated.
 */
import { useQuery, useMutation } from '@tanstack/react-query'
import { paymentsApi } from '../lib/api.js'
import { Button, Empty, Spinner, toast } from '../components/ui/index.js'
import { useI18n } from '../lib/i18n.js'

export function Payments() {
  const { t } = useI18n()

  const { data, isLoading } = useQuery({
    queryKey: ['payments-connect'],
    queryFn: () => paymentsApi.status(),
    refetchInterval: 15_000,
  })

  const startMut = useMutation({
    mutationFn: () => paymentsApi.connect(window.location.origin + window.location.pathname),
    onSuccess: ({ onboarding_url }) => {
      // navegação top-level ao Stripe (o link vale UMA vez; voltar recria)
      window.location.assign(onboarding_url)
    },
    onError: (e: Error) => toast.error(e.message),
  })

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner className="w-6 h-6 text-indigo-500" />
      </div>
    )
  }
  if (!data) return <Empty title={t('admin.common.error')} description="" />

  const badge = (ok: boolean) => (
    <span
      data-testid={`payments-flag-${ok ? 'on' : 'off'}`}
      className={ok ? 'text-green-600 font-bold' : 'text-gray-400 font-bold'}
    >
      {ok ? '●' : '○'}
    </span>
  )

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('admin.payments.title')}</h1>
        <p className="text-sm text-gray-500">{t('admin.payments.subtitle')}</p>
      </div>

      <div
        data-testid="payments-status"
        className="rounded-xl border border-gray-200 bg-white p-4 space-y-3"
      >
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-bold text-gray-900">
            {data.ready ? '✅' : data.connected ? '⏳' : '⚪'}{' '}
            {data.ready
              ? t('admin.payments.state_ready')
              : data.connected
                ? t('admin.payments.state_pending')
                : t('admin.payments.state_off')}
          </p>
          {data.account_id && <code className="text-xs text-gray-500">{data.account_id}</code>}
        </div>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>
            {badge(data.charges_enabled)} {t('admin.payments.charges')}
          </li>
          <li>
            {badge(data.payouts_enabled)} {t('admin.payments.payouts')}
          </li>
          <li>
            {badge(data.details_submitted)} {t('admin.payments.details')}
          </li>
        </ul>
        {data.country_caveat && (
          <p
            data-testid="payments-country-caveat"
            className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-2"
          >
            ⚠ {data.country_caveat}
          </p>
        )}
        <Button
          data-testid="payments-start"
          onClick={() => startMut.mutate()}
          disabled={startMut.isPending}
        >
          {startMut.isPending
            ? t('admin.common.saving')
            : data.connected
              ? t('admin.payments.resume')
              : t('admin.payments.start')}
        </Button>
        <p className="text-xs text-gray-400">{t('admin.payments.hint')}</p>
      </div>
    </div>
  )
}
